import { Box, Typography } from '@mui/material';
import { cardSurfaceSx } from '../../theme';
import type { AccessKeyGuideProps } from './types';

/**
 * How to make an access key safely, for the host a repository is on. **A private
 * repository cannot be read without one**, and GitHub answers 404 for a private
 * repository it will not describe, so this is what an admin is shown instead of a
 * bare "not found".
 *
 * The safe key is the narrowest one: fine-grained, one owner, only the repositories
 * being added, read-only contents, and an expiry. **A classic token is refused by the
 * service** because it reads everything its owner can.
 *
 * **A GitHub App is the better connection** where an organisation owner will make one:
 * it belongs to the organisation and not to a person who may leave, and its private
 * key does not expire.
 */

const GITHUB_STEPS: string[] = [
  'On GitHub, open Settings, then Developer settings, then Personal access tokens, then Fine-grained tokens, and choose Generate new token.',
  'Set the resource owner to the organisation that owns the repository, such as and-digital, not your own account.',
  'Under Repository access, choose Only select repositories, and pick just the ones you are adding to the toolkit.',
  'Under Repository permissions, set Contents to Read-only. GitHub adds read-only Metadata by itself. Grant nothing else.',
  'Set an expiry, and enter the same date when you add the key here, so the page can show it. A key with no expiry is one nobody remembers.',
  'If the organisation requires approval for fine-grained tokens, an organisation owner has to approve yours before it can read an internal or private repository.',
  'Copy the token once and paste it into the form here. It goes straight to the toolkit\'s secret store and is never shown again. Do not paste it anywhere else: not Slack, not a ticket, not a note.',
];

type Part = { heading?: string; steps: string[] };

/**
 * **Three parts, because three different people may do them**: an organisation owner
 * makes the app and installs it, the admin here pastes its key, and an owner is needed
 * again whenever another repository is to be read. The labels are GitHub's own.
 */
const GITHUB_APP_PARTS: Part[] = [
  {
    heading: '1. Create the app',
    steps: [
      'An owner of the GitHub organisation, or someone the organisation has made an app manager, clicks their profile picture, then Your organizations, then Settings next to the organisation, such as and-digital. Do this from the organisation\'s settings and not your own: an app owned by a person goes when they do, and your own Settings would make exactly that. The form says whose it is under Where can this GitHub App be installed. If it names a person, you are in the wrong place.',
      'In the sidebar, choose Developer settings, then GitHub Apps, then New GitHub App.',
      'GitHub App name: anything unique across GitHub and 34 characters or fewer, such as the organisation name and toolkit. Homepage URL: the address of this toolkit. Description is optional.',
      'Identifying and authorizing users: leave Callback URL empty, because the toolkit never asks anyone to sign in with GitHub, and GitHub ignores it for an app that needs no user token. Leave Expire user authorization tokens as it is. Leave Request user authorization (OAuth) during installation and Enable Device Flow unticked.',
      'Post installation: leave Setup URL empty and Redirect on update unticked.',
      'Webhook: untick Active, because the toolkit only reads. Leave Webhook URL and Secret empty.',
      'Permissions: under Repository permissions, find Contents and set it to Read-only. GitHub adds read-only Metadata by itself. Leave every other repository permission, and all of Organization permissions, Account permissions and Enterprise permissions, on No access.',
      'Subscribe to events: tick none. The toolkit reads on a schedule and is not told about anything.',
      'Where can this GitHub App be installed: choose Only on this account, which should name the organisation, then choose Create GitHub App.',
      'On the app\'s settings page, copy the App ID from the top. In the sidebar choose Credentials, then under Key pairs choose New key. GitHub downloads a .pem file, and that download is the only copy.',
      'If you made the app under your own account by mistake, it is not lost: in its settings choose Advanced, then Transfer ownership, type the organisation\'s name, pick the organisation from the dropdown, and choose Transfer this GitHub App.',
    ],
  },
  {
    heading: '2. Install it on the repositories the toolkit should read',
    steps: [
      'On the app\'s settings page, choose Install App, then Install next to the organisation.',
      'Under Repository access, choose Only select repositories, open the Select repositories dropdown and pick them, then choose Install. All repositories would let the toolkit read every repository the organisation has, which is more than it needs.',
      'A repository that is not in the installation cannot be read. Check will say the app is not installed on it.',
      'Paste the App ID and the whole contents of the .pem file into the form here, and save. The key goes straight to the toolkit\'s secret store and is never shown again. Then delete the downloaded file, and do not paste it anywhere else: not Slack, not a ticket, not a note.',
    ],
  },
  {
    heading: 'Later: adding another repository',
    steps: [
      'An organisation owner opens the organisation\'s Settings, and under Third-party Access chooses GitHub Apps.',
      'Next to the app, choose Configure. Under Repository access, open the Select repositories dropdown, add the repository, and choose Save.',
      'Then Check the repository again here. Nothing needs to change in the toolkit.',
    ],
  },
];

/**
 * **A project access token is the better one**: it belongs to a bot and not to a person, so it
 * survives whoever made it leaving. On GitLab.com it needs a Premium or Ultimate plan, so a
 * personal token is the fallback. Both expire, and GitLab has no app to install.
 *
 * **`read_api`, not `read_repository`**, because GitLab documents `read_repository` for
 * Git-over-HTTP and the repository files API, and the toolkit also reads tags and the file tree.
 */
const GITLAB_PARTS: Part[] = [
  {
    heading: '1. Make a project access token',
    steps: [
      'You need the Maintainer or Owner role on the project. On GitLab.com, project access tokens need a Premium or Ultimate plan; if there is none, use a personal access token below.',
      'In the GitLab project, open Settings, then Access tokens, and choose Add new token.',
      'Enter a name and an expiry date, choose the Reporter role, and tick read_api and nothing else.',
      'Choose Create project access token, and copy the token. It is shown once.',
    ],
  },
  {
    heading: 'Or make a personal access token',
    steps: [
      'Select your avatar, then Edit profile, then Access, then Personal access tokens.',
      'From the Generate token dropdown choose Legacy token, enter a Token name and an Expiration date, tick read_api and nothing else, and choose Generate token.',
      'A personal token reads everything its owner can, which is more than the toolkit needs, and it stops when its owner leaves. Use a project access token where you can.',
    ],
  },
  {
    heading: '2. Add it here',
    steps: [
      'Paste the token into the form here, and enter the same expiry date, so the page can show it. GitLab does not tell us, and sets one a year out if you leave it blank.',
      'The token goes straight to the toolkit\'s secret store and is never shown again. Do not paste it anywhere else: not Slack, not a ticket, not a note.',
    ],
  },
];

const GUIDES: Record<'token' | 'app' | 'gitlab', { parts: Part[]; title: string }> = {
  token: { parts: [{ steps: GITHUB_STEPS }], title: 'Set up a GitHub access key' },
  app: { parts: GITHUB_APP_PARTS, title: 'Set up a GitHub App' },
  gitlab: { parts: GITLAB_PARTS, title: 'Set up a GitLab access key' },
};

export const AccessKeyGuide = ({ host, kind = 'token' }: AccessKeyGuideProps) => {
  const { parts, title } = GUIDES[host === 'gitlab' ? 'gitlab' : kind];

  return (
    <Box component="section" aria-label={title} sx={{ ...cardSurfaceSx, padding: 6 }}>
      <Typography variant="h3" component="h2">
        {title}
      </Typography>

      {host === 'gitlab' && (
        <Typography role="note" variant="body2" sx={{ color: 'text.secondary', marginTop: 2 }}>
          These steps follow GitLab&apos;s documentation. Importing from GitLab has not been run against a real project
          yet.
        </Typography>
      )}

      {parts.map((part) => (
        <Box key={part.heading ?? 'steps'} sx={{ marginTop: 4 }}>
          {part.heading !== undefined && (
            <Typography variant="body1" component="h3" sx={{ fontWeight: 600 }}>
              {part.heading}
            </Typography>
          )}
          <Box component="ol" sx={{ margin: 0, marginTop: 2, paddingLeft: 6, display: 'flex', flexDirection: 'column', gap: 2 }}>
            {part.steps.map((step) => (
              <Typography component="li" variant="body2" key={step}>
                {step}
              </Typography>
            ))}
          </Box>
        </Box>
      ))}

      <Typography variant="body2" sx={{ color: 'text.secondary', marginTop: 4 }}>
        Whoever adds a key is recorded against it, and is the person the toolkit asks when it stops
        working.
      </Typography>
    </Box>
  );
};
